/** Who can see a hero who does not want to be seen.
 *
 * Stealth is a `Modifier` stat like every other, so a class passive, an
 * artifact or a future secondary skill can all grant it through one mechanism.
 * What is unusual is where it is READ: not in a rule that decides an outcome,
 * but in the two places that decide what a player is told - `observe.ts`, which
 * builds the observation, and `visibility.ts`, which writes the fog memory. A
 * hero hidden from the first and remembered by the second would be invisible
 * for exactly one turn and then permanently pinned on the minimap, which is
 * worse than not hiding at all.
 *
 * The precedent is `disguisedLevel`, which already hides a hero's STRENGTH from
 * the observation. This is the same idea one step further: it hides the hero.
 *
 * The reveal rule is about the threat's size, not the watcher's skill
 * ------------------------------------------------------------------
 * A stealthed hero is seen anyway once his army is `ratio` times the strength
 * of the watcher's best hero. That is deliberately not a detection stat: the
 * question it answers is "can an army big enough to destroy me creep up on me
 * unannounced", and the answer is no. A scout with nothing in his pockets stays
 * invisible to anybody; a doomstack does not, however quiet it is.
 *
 * The ratio is asymmetric ON PURPOSE
 * ----------------------------------
 * Two for a human watching, four for a computer watching. It is a difficulty
 * knob and not a bug, and it is written down here so that nobody later
 * "corrects" it into symmetry: a human is told about a force twice his size,
 * and may hide his own until it is four times the computer's. The player is
 * meant to win the scouting game; what stealth must never do is let the
 * computer delete a human army it never had a chance to see coming.
 */

import type { ContentRegistry } from "../content/registry.js";
import type { HeroState, PlayerKind } from "../state.js";
import type { PlayerId } from "../ids.js";
import type { Ctx } from "./ctx.js";
import { armyValue, statFor } from "./hero.js";

/** A human is warned about a force twice his best hero's. */
export const STEALTH_RATIO_HUMAN = 2;
/** A computer is not warned until it is four times. See the note above. */
export const STEALTH_RATIO_AI = 4;

export function stealthRatioFor(kind: PlayerKind | undefined): number {
  return kind === "ai" ? STEALTH_RATIO_AI : STEALTH_RATIO_HUMAN;
}

/** Non-zero when anything on this hero grants stealth. */
export function stealthOf(c: Ctx, h: HeroState): number {
  return statFor(c, h, "stealth", 0).value;
}

/** The strongest army this player has in the field, in `aiValue`. */
export function bestFieldArmy(c: Ctx, content: ContentRegistry, p: PlayerId): number {
  let best = 0;
  const pl = c.s.players[p];
  if (pl === undefined) return 0;
  for (const id of pl.heroes) {
    const h = c.s.heroes[id];
    if (h === undefined) continue;
    const v = armyValue(content, c.s.armies[h.army] ?? null);
    if (v > best) best = v;
  }
  return best;
}

/**
 * Is this hero hidden from `viewer` right now?
 *
 * False for the hero's own owner, for a spectator (handled by the callers,
 * which never ask), and for every hero with no stealth at all - which is all
 * but a handful, so the cheap test comes first.
 *
 * A watcher with no hero in the field is told everything. There is nothing to
 * creep up on, and the alternative reading - "nobody can see you at all" -
 * would make a kingdom that has just lost its last hero blind as well as
 * beaten.
 */
export function hiddenFrom(
  c: Ctx, content: ContentRegistry, h: HeroState, viewer: PlayerId,
): boolean {
  if (h.owner === viewer) return false;
  if (stealthOf(c, h) <= 0) return false;
  const watching = bestFieldArmy(c, content, viewer);
  if (watching <= 0) return false;
  const carried = armyValue(content, c.s.armies[h.army] ?? null);
  return carried < stealthRatioFor(c.s.players[viewer]?.kind) * watching;
}
